// The instrument widgets' templates (contract §8.3): tile, gauge, dial, level,
// sparkline and chart, each the body WidgetCard draws today
// (cloud/web/src/components/dashboard/widgets.tsx and the chart components it
// uses: Readout, ArcGauge, CompassRose, LevelMeter, TrendReadout,
// HistoryChart). The card around it is the app's panel card with the widget's
// icon (§6.2); a widget the generator subtitled (the device that measures it)
// has the device as the body's first line (`data-tc-device`), and one it did
// not (a sparkline) has none, as today.
//
// A widget of several paths shows its first as the widget does, and each
// other below it as a small named readout: every reading a page declares
// must be shown (§9.6). The generator's widgets have one path, the chart
// widget aside.
//
// Where the rendered check asks more than today's widget: the dial's value is
// the reading's own text (the dial wrote a three-figure bearing, and "P" or
// "S" on a relative angle, which no binding writes), and the sparkline marks
// its low and high with dots, not 11 px labels, since a value is at least
// 14 px (§9.6). A relative dial leaves out the ring's two "90" labels, which
// sit on the value's line and read as a number beside it (§9.4). A gauge
// prints its scale's ends only when both are whole numbers: "11.5" printed
// as text is a typed-in value (§9.4), and no binding writes a scale's end.
// An angle's sparkline and chart lines take its range and no area (§4.5.4).
//
// With no reading, each keeps its face (Mike, 2026-10-14, TOM-665): the
// gauge's arc, ticks and ends, the dial's rose, the level's track and the
// readout's unit, with "—" in muted ink for the value and no fill, needle or
// "now" mark, so nothing reads as a value. A sparkline or chart draws what
// its window holds whatever the reading now, as a record; a sparkline whose
// window holds nothing draws a dashed rule where its line would be.
//
// The Marina look (cloud ADR 0396, TOM-678): each widget takes its first
// reading's kind (`data-domain`, domains.js): the gauge's fill, the dial's
// needle and north, the level's bar, and the sparkline's and chart's line,
// area and marks, with the area fading from the kind's top opacity to its
// foot. Numerals are 700. The sparkline and the chart, which draw a stored
// 24 h history, show the "▲ 24H" range chip. A level past its line keeps its
// kind's colour and says so with the warning mark and its word, in the
// warning's tone; a stale reading's marks are grey.

import { pageLinearTicks } from "../page-rules.js";
import {
  AREA_CSS,
  CHIP_CSS,
  FRESHNESS_CSS,
  FRESHNESS_LINE,
  LOW_GOOD_PATH,
  READOUT_CSS,
  angleRange,
  areaGradient,
  css,
  defaultScaleFor,
  domainCss,
  domainOf,
  domainsOf,
  esc,
  h,
  n,
  output,
  pathFacts,
  rangeChip,
  readingNames,
  reading,
  readout,
  QUARTER_GAPS,
  warnMark,
} from "./kit.js";
import { pageDomainFor } from "./domains.js";
import { historyChart, historyCss } from "./chart-trend.js";

/** The widgets a template draws, in WidgetBody's order. */
export const INSTRUMENT_WIDGETS = Object.freeze([
  "gauge",
  "dial",
  "level",
  "tile",
  "sparkline",
  "chart",
]);

const COMMON_CSS = css(`
  .in { gap: 4px; }
  .in-dv { font-size: 12px; color: var(--muted); overflow: hidden; text-overflow: ellipsis;
    white-space: nowrap; min-height: 0; }
  .in-dv:empty { display: none; }
  .in-mid { flex: 1 1 auto; display: flex; align-items: center; justify-content: center;
    min-height: 0; position: relative; width: 100%; }
  .in-more { display: flex; flex-direction: column; gap: 4px; flex: none; }
  .in-more > div { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
  .in-more .nm { font-size: 13px; color: var(--muted); overflow: hidden; text-overflow: ellipsis;
    white-space: nowrap; min-width: 0; }
  .in .rd-v[data-quality=missing] ~ .rd-u:not(:empty) { display: inline; }
`);

/**
 * A widget's scale, its range or its default, in store units. A dial is a
 * compass (0° to 360°) unless its range starts below 0 (−180° to 180°), as
 * DialBody reads it: it turns by the value, never by the range's ends.
 */
function scaleOf(spec, path, context) {
  const r = spec.range;
  const unit = pathFacts(context, path).storeUnit;
  if (spec.widget === "dial") {
    const full = defaultScaleFor(path, unit, "dial");
    return r && typeof r.min === "number" && r.min < 0
      ? [-full[1] / 2, full[1] / 2]
      : full;
  }
  if (
    r &&
    typeof r.min === "number" &&
    typeof r.max === "number" &&
    Number.isFinite(r.min) &&
    Number.isFinite(r.max) &&
    r.max > r.min
  )
    return [r.min, r.max];
  return defaultScaleFor(path, unit, spec.widget);
}

/** A scale end as the gauge prints it (formatValue: no decimals from 100, one below). */
function scaleText(v) {
  const abs = Math.abs(v);
  if (abs >= 10_000) {
    const [scaled, suffix] = abs >= 1_000_000 ? [v / 1e6, "M"] : [v / 1e3, "K"];
    return `${trimZero(scaled.toFixed(Math.abs(scaled) >= 100 ? 0 : 1))}${suffix}`;
  }
  return trimZero(v.toFixed(abs >= 100 ? 0 : 1));
}
function trimZero(s) {
  const cleaned = s.includes(".") ? s.replace(/\.?0+$/, "") : s;
  return cleaned === "-0" ? "0" : cleaned;
}

/** Bearing-style polar point: 0 up, clockwise, degrees. */
function polar(cx, cy, r, deg) {
  const a = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/** charts/lib/arc.ts's arcPath: an arc from a0 to a1, stroke-ready. */
function arcPath(cx, cy, r, a0, a1) {
  const f = (v) => String(Math.round(v * 100) / 100);
  const s = polar(cx, cy, r, a0);
  const e = polar(cx, cy, r, a1);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M${f(s.x)},${f(s.y)}A${f(r)},${f(r)} 0 ${large} 1 ${f(e.x)},${f(e.y)}`;
}

/** The other paths of a widget, each a small named readout. */
function extras(names) {
  if (names.length === 0) return "";
  return h(
    "div",
    { class: "in-more" },
    names.map((name) =>
      h(
        "div",
        {},
        h("span", { class: "nm", "data-tc-name": name }),
        readout(name, 15),
      ),
    ),
  );
}

function tile(names) {
  const [first, ...rest] = names;
  return {
    html: [
      h(
        "div",
        { class: "in-mid" },
        h("div", { class: "rd" }, readout(first, 34, "tl-row")),
      ),
      extras(rest),
    ],
    css: css(`html[data-size=l] .tl-row { font-size: 46px !important; }`),
  };
}

// ArcGauge: a 240° arc in a 200 × 158 box, its ink numeral centred inside.
const G = { cx: 100, cy: 96, r: 80, w: 10, a0: -120, a1: 120 };

/**
 * A value's size so it fits the room its drawing leaves (TOM-665): the
 * numeral at most its row's size (1em), smaller as its text is longer
 * (`--tc-chars`, which the host writes on a value's element), never under
 * 14 px. `room` is the width, a CSS length, and `unit` the px the unit and
 * its gap take beside the numeral. Each character is taken as 0.66 em, a
 * digit's width in the rounded face or its fallback, a little over; a value still too wide
 * at 14 px moves its unit below it, as the readout's row wraps.
 */
function fitSize(room, unit) {
  return `clamp(14px, (${room} - ${unit}px) / (var(--tc-chars, 4) * 0.66), 1em)`;
}

// The gauge's value: centred 9 units under the box's middle (88, the arc's
// centre at 96), in 116 units, the arc's inner width inside its ticks at the
// numeral's top and foot; the unit beside it 32 px ("rpm" at 13.6 px and its
// gap).
const GAUGE_FIT = { dy: 9, w: 116, unit: 32 };

function gauge(names, scale) {
  const [first, ...rest] = names;
  const [min, max] = scale;
  const track = arcPath(G.cx, G.cy, G.r, G.a0, G.a1);
  const ticks = pageLinearTicks(min, max, 4).filter((t) => t > min && t < max);
  const tickLines = ticks.map((v) => {
    const a = G.a0 + ((v - min) / (max - min)) * (G.a1 - G.a0);
    const p1 = polar(G.cx, G.cy, G.r - G.w / 2 - 3, a);
    const p2 = polar(G.cx, G.cy, G.r - G.w / 2 - 7, a);
    return h("line", {
      class: "gg-tk",
      x1: n(p1.x),
      y1: n(p1.y),
      x2: n(p2.x),
      y2: n(p2.y),
    });
  });
  const lo = polar(G.cx, G.cy, G.r, G.a0);
  const hi = polar(G.cx, G.cy, G.r, G.a1);
  const ends = [scaleText(min), scaleText(max)];
  const whole = ends.every((t) => /^-?\d+$/.test(t));
  const svg = h(
    "svg",
    { class: "gg", viewBox: "0 0 200 158", "aria-hidden": "true" },
    h("path", { class: "gg-track", d: track }),
    tickLines,
    h("path", {
      class: "gg-fill",
      d: track,
      pathLength: 1,
      "data-tc-frac": first,
    }),
    whole
      ? [
          h(
            "text",
            { class: "gg-end", x: n(lo.x - 2), y: n(lo.y + 18) },
            ends[0],
          ),
          h(
            "text",
            { class: "gg-end", x: n(hi.x + 2), y: n(hi.y + 18) },
            ends[1],
          ),
        ]
      : "",
  );
  return {
    html: [
      h(
        "div",
        { class: "in-mid gg-box" },
        svg,
        h(
          "div",
          { class: "gg-c" },
          h("div", { class: "rd" }, readout(first, 34)),
        ),
      ),
      extras(rest),
    ],
    css: css(`
      .gg-box { height: 150px; flex: none; margin: auto 0; container: gg / size; }
      html[data-size=l] .gg-box { height: 190px; }
      .gg { display: block; width: 100%; height: 100%; overflow: visible; }
      .gg-track { fill: none; stroke: var(--track); stroke-width: 10; stroke-linecap: round; }
      .gg-tk { stroke: var(--quiet); stroke-width: 1; }
      .gg-fill { fill: none; stroke: var(--_c); stroke-width: 10; stroke-linecap: round;
        stroke-dasharray: 1 2; stroke-dashoffset: calc(1 - var(--frac,0)); }
      .gg-fill[data-quality=missing] { display: none; }
      .gg-end { font-size: 11px; fill: var(--muted); text-anchor: middle; font-variant-numeric: tabular-nums; }
      .gg-c { position: absolute; left: 50%; top: calc(50% + ${GAUGE_FIT.dy} * var(--_k));
        width: calc(${GAUGE_FIT.w} * var(--_k)); transform: translate(-50%, -50%);
        display: flex; flex-direction: column; align-items: center; text-align: center;
        --_k: min(${n(100 / 200)}cqw, ${n(100 / 158)}cqh); }
      .gg-c .rd { align-items: center; max-width: 100%; }
      .gg-c .rd-row { justify-content: center; }
      .gg-c .rd-v { font-size: ${fitSize(`${GAUGE_FIT.w} * var(--_k)`, GAUGE_FIT.unit)}; }
    `),
  };
}

// CompassRose: a 220 × 220 ring, the needle under a centre disc.
const C = 110;

// The dial's value: in 92 units, the disc's inner width (r 54) at the
// numeral's top and foot; the unit beside it 14 px ("°" and its gap), a wider
// one wrapping below.
const DIAL_FIT = { w: 92, unit: 14 };

function dial(names, scale) {
  const [first, ...rest] = names;
  const relative = scale[0] < 0;
  // The ring's ticks as two paths, minor and major, and north's own.
  let minor = "";
  for (let a = 0; a < 360; a += 10) {
    if (a % 30 === 0) continue;
    const p1 = polar(C, C, 90, a);
    const p2 = polar(C, C, 96, a);
    minor += `M${n(p1.x)} ${n(p1.y)}L${n(p2.x)} ${n(p2.y)}`;
  }
  let major = "";
  for (let a = relative ? 0 : 30; a < 360; a += 30) {
    const p1 = polar(C, C, 87, a);
    const p2 = polar(C, C, 96, a);
    major += `M${n(p1.x)} ${n(p1.y)}L${n(p2.x)} ${n(p2.y)}`;
  }
  const minors = [h("path", { class: "dl-mi", d: minor })];
  const majors = [
    h("path", { class: "dl-ma", d: major }),
    relative
      ? ""
      : h("path", { class: "dl-n", d: `M${C} ${C - 87}L${C} ${C - 96}` }),
  ];
  const labels = [];
  const label = (a, text, cardinal, north = false) => {
    const p = polar(C, C, 74, a);
    labels.push(
      h(
        "text",
        {
          class: cardinal ? (north ? "dl-c dl-cn" : "dl-c") : "dl-m",
          x: n(p.x),
          y: n(p.y + (cardinal ? 4.5 : 3.5)),
        },
        text,
      ),
    );
  };
  if (!relative) {
    for (const [a, letter] of [
      [0, "N"],
      [90, "E"],
      [180, "S"],
      [270, "W"],
    ])
      label(a, letter, true, letter === "N");
    for (let a = 30; a < 360; a += 30)
      if (a % 90 !== 0) label(a, String(a).padStart(3, "0"), false);
  } else {
    for (let a = -150; a <= 180; a += 30) {
      if (a === 0 || a === 180) label(a, a === 0 ? "0" : "180", true);
      else if (Math.abs(a) !== 90) label(a, String(Math.abs(a)), false);
    }
  }
  const svg = h(
    "svg",
    { class: "dl", viewBox: "0 0 220 220", "aria-hidden": "true" },
    minors,
    majors,
    labels,
    h(
      "g",
      { class: "dl-ndl", "data-tc-frac": first },
      h("polygon", {
        // From the ring to the disc's edge (TOM-1001): the disc is no longer
        // painted over the needle's foot, so the foot stops at it.
        points: "0,-84 3.4,-54 -3.4,-54",
        transform: `translate(${C},${C})`,
      }),
    ),
    h("circle", { class: "dl-disc", cx: C, cy: C, r: 54 }),
  );
  return {
    html: [
      h(
        "div",
        { class: "in-mid dl-box" },
        h(
          "div",
          { class: "dl-sq" },
          svg,
          h(
            "div",
            { class: "dl-c0" },
            h("div", { class: "rd" }, readout(first, 24)),
          ),
        ),
      ),
      extras(rest),
    ],
    css: css(`
      .dl-box { height: 150px; flex: none; margin: auto 0; }
      html[data-size=l] .dl-box { height: 190px; }
      .dl-sq { position: relative; aspect-ratio: 1; height: 100%; max-width: 100%; container: dl / size; }
      .dl { display: block; width: 100%; height: 100%; }
      .dl-mi { fill: none; stroke: var(--track); stroke-width: 1; }
      .dl-ma { fill: none; stroke: var(--grid); stroke-width: 1.5; }
      .dl-n { stroke: var(--_c); stroke-width: 2.5; }
      .dl-c { font-size: 14px; font-weight: 600; fill: var(--muted); text-anchor: middle; }
      .dl-cn { fill: var(--_c); }
      .dl-m { font-size: 10.5px; fill: var(--quiet); text-anchor: middle; font-variant-numeric: tabular-nums; }
      .dl-ndl { fill: var(--_c); transform-origin: ${C}px ${C}px;
        transform: rotate(calc((${relative ? -180 : 0} + var(--frac,0) * 360) * 1deg)); }
      .dl-ndl[data-quality=missing] { display: none; }
      .dl-disc { fill: none; stroke: var(--track); stroke-width: 1; }
      .dl-c0 { position: absolute; left: 50%; top: 50%; width: calc(${DIAL_FIT.w} * var(--_k));
        transform: translate(-50%, -50%); display: flex; flex-direction: column; align-items: center;
        text-align: center; --_k: min(${n(100 / 220)}cqw, ${n(100 / 220)}cqh); }
      .dl-c0 .rd { align-items: center; max-width: 100%; }
      .dl-c0 .rd-row { justify-content: center; }
      .dl-c0 .rd-v { font-size: ${fitSize(`${DIAL_FIT.w} * var(--_k)`, DIAL_FIT.unit)}; }
    `),
  };
}

function level(names, lowGood) {
  const [first, ...rest] = names;
  return {
    html: [
      h(
        "div",
        { class: "in-mid" },
        h(
          "div",
          { class: "lv", "data-tc-state": first },
          h(
            "div",
            { class: "lv-track" },
            h("div", { class: "lv-fill", "data-tc-frac": first }),
          ),
          h(
            "span",
            { class: "lv-v" },
            h("span", {
              class: "lv-n",
              "data-tc-text": first,
              "data-tc-decimals": "0",
            }),
            h("span", { class: "rd-u", "data-tc-unit": first }),
            // A level past its line is named in words and the warning mark,
            // never by colour alone (LevelMeter, DESIGN_SYSTEM §7): "Low" for
            // a tank whose healthy end is full.
            h(
              "span",
              { class: "lv-flag" },
              warnMark("lv-wm"),
              lowGood ? "High" : "Low",
            ),
          ),
        ),
      ),
      extras(rest),
    ],
    css: css(`
      .lv { display: flex; align-items: center; gap: 12px; width: 100%; min-width: 0; --_tone: var(--attention); }
      .lv[data-zone=bad] { --_tone: var(--danger); }
      .lv-track { position: relative; flex: 1; height: 8px; border-radius: 999px; background: var(--track); min-width: 40px;
        mask-image: ${QUARTER_GAPS}; }
      .lv-fill { position: absolute; inset: 0 auto 0 0; border-radius: 999px; background: var(--_c);
        width: calc(var(--frac,0) * 100%); transition: width 0.5s cubic-bezier(0.25,0.8,0.35,1); }
      .lv-fill[data-quality=missing] { display: none; }
      .lv-v { flex: none; display: inline-flex; align-items: baseline; gap: 0.18em;
        font-family: var(--tc-font-rounded); font-size: 14px; font-weight: 600; color: var(--text); }
      .lv-n[data-quality=missing] { color: var(--muted); font-weight: 500; }
      .lv-flag { display: none; align-items: center; gap: 4px; margin-left: 6px; font-size: 12px; font-weight: 600; color: var(--_tone); }
      .lv-wm { width: 14px; height: 14px; flex: none; }
      .lv[data-zone=warn] .lv-flag, .lv[data-zone=bad] .lv-flag { display: inline-flex; }
    `),
  };
}

/** The level's warn and bad lines (LevelMeter's 25% and 10% of the unhealthy end), as zones. */
function levelZones(path, [min, max]) {
  const span = max - min;
  if (LOW_GOOD_PATH.test(path))
    return [
      { name: "bad", above: max - 0.1 * span },
      { name: "warn", above: max - 0.25 * span },
    ];
  return [
    { name: "bad", below: min + 0.1 * span },
    { name: "warn", below: min + 0.25 * span },
  ];
}

function sparkline(names, angle) {
  const [first, ...rest] = names;
  // 4 px all round (TOM-1000): room for the 3 px marks' rings at every size.
  // The old 16 px top and bottom (room for 11 px extreme labels the template
  // never draws) left a small sparkline's 24 px drawing a plot −8 px tall,
  // so its trend drew upside down.
  const inset = "4 4 4 4";
  const series = {
    "data-tc-points": "96",
    "data-tc-group": "s",
    "data-tc-inset": inset,
  };
  const svg = h(
    "svg",
    {
      class: "sp",
      "data-tc-fit": "spark",
      "data-tc-window": "24h",
      "aria-hidden": "true",
    },
    h("defs", {}, areaGradient("sp-a")),
    angle.has(first)
      ? ""
      : h("path", {
          class: "sp-area",
          "data-tc-path": first,
          "data-tc-shape": "area",
          ...series,
          fill: "url(#sp-a)",
        }),
    h("path", { class: "sp-line", "data-tc-path": first, ...series }),
    h("circle", {
      class: "sp-x sp-hi",
      cx: 0,
      cy: 0,
      r: 3,
      "data-tc-mark": first,
      "data-tc-at": "max",
      ...series,
    }),
    h("circle", {
      class: "sp-x sp-lo",
      cx: 0,
      cy: 0,
      r: 3,
      "data-tc-mark": first,
      "data-tc-at": "min",
      ...series,
    }),
    h("circle", {
      class: "sp-now",
      cx: 0,
      cy: 0,
      r: 3,
      "data-tc-mark": first,
      "data-tc-at": "last",
      ...series,
    }),
    // Where the line would be when its window holds nothing (TOM-665).
    h("line", { class: "sp-nd", x2: 1 }),
  );
  const at = (dy = 0) =>
    `translate(calc(4px + var(--x,0) * (var(--tc-w,200) - 8) * 1px), calc(4px + (1 - var(--y,0)) * (var(--tc-h,52) - 8) * 1px${dy ? ` + ${dy}px` : ""}))`;
  return {
    html: [
      h(
        "div",
        { class: "sp-head" },
        h("div", { class: "rd" }, readout(first, 34, "sp-row")),
        rangeChip(),
      ),
      extras(rest),
      svg,
    ],
    css: css(`
      .sp-head { flex: none; margin-bottom: 6px; padding-top: 6px; display: flex; align-items: flex-start;
        justify-content: space-between; gap: 8px; }
      .sp-head .rc { margin-top: 4px; }
      html[data-size=s] .sp-row { font-size: 24px !important; }
      .sp { display: block; width: 100%; height: 52px; flex: none; margin-top: auto; overflow: visible; }
      html[data-size=s] .sp { height: 24px; }
      .sp-area { stroke: none; }
      .sp-line { fill: none; stroke: var(--_c); stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
      .sp-now { fill: var(--_c); transform: ${at()}; }
      .sp-x { fill: var(--_c); stroke: var(--background); stroke-width: 2; }
      .sp-hi { transform: ${at()}; }
      .sp-lo { transform: ${at()}; }
      html[data-size=s] .sp-x { display: none; }
      .sp circle[data-quality=missing] { display: none; }
      .sp-nd { display: none; stroke: var(--grid); stroke-width: 1.5; stroke-dasharray: 4 4;
        vector-effect: non-scaling-stroke;
        transform: translate(4px, calc(var(--tc-h,52) * 0.5px)) scaleX(calc(var(--tc-w,200) - 8)); }
      .sp-line[d=""] ~ .sp-nd { display: inline; }
    `),
  };
}

/** web widgets.tsx's shortPathLabel: the path's last two segments, in words. */
function shortPathLabel(path) {
  const last = path.split(".").slice(-2).join(" ");
  return last.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}

function chart(names, byPath, context, angle) {
  // One panel per unit, as the chart widget splits unlike dimensions: by the
  // path's store unit here (the owner's units convert within a unit), an
  // angle apart, on its own range (§4.5.3).
  const panels = new Map();
  for (const [path, name] of byPath) {
    const facts = pathFacts(context, path);
    const key = `${(facts.storeUnit ?? "").toLowerCase()}${facts.angle ? " angle" : ""}`;
    if (!panels.has(key)) panels.set(key, []);
    panels.get(key).push({ path, name });
  }
  const list = [...panels.values()];
  const height = list.length > 1 ? Math.max(150, 300 - list.length * 30) : 260;
  const parts = list.map((members, i) => {
    // historyLegend's markup and CSS, each key named by its path (TOM-665).
    const legend =
      list.length > 1 || members.length > 1
        ? h(
            "div",
            { class: "hc-lg" },
            members.map((m, j) =>
              h(
                "span",
                {},
                h("i", { class: `hc-k hc-k${Math.min(3, j + 1)}` }),
                esc(shortPathLabel(m.path)),
              ),
            ),
          )
        : "";
    return h(
      "div",
      { class: "ic-panel", ...domainOf(pageDomainFor(members[0].path)) },
      legend,
      historyChart({
        p: "hc",
        fit: `plot-${i + 1}`,
        group: `y${i + 1}`,
        names: members.map((m) => m.name),
        window: "24h",
        points: 400,
        angle,
      }),
    );
  });
  return {
    // Each panel takes its first series' kind; the others are dashed ink.
    domains: domainsOf(list.map((members) => members[0].path)),
    html: [
      h("div", { class: "ic-h" }, rangeChip()),
      h("div", { class: "ic" }, parts),
    ],
    css:
      css(`
        .ic-h { display: flex; justify-content: flex-end; flex: none; }
        .ic { display: flex; flex-direction: column; gap: 8px; width: 100%; }
        .ic-panel { display: flex; flex-direction: column; gap: 4px; }
        .ic .hc { height: ${height}px; flex: none; }
      `) + historyCss("hc"),
  };
}

/** instrument.<widget>: the widget's body (§8.3). */
export function instrumentTemplate(source, context) {
  const spec = source.spec;
  const widget = spec.widget;
  const paths = (Array.isArray(spec.paths) ? spec.paths : []).slice(0, 6);
  const byPath = readingNames(paths);
  const names = [...byPath.values()];
  const first = paths[0];
  const scaled = widget === "gauge" || widget === "dial" || widget === "level";
  const scale = scaled ? scaleOf(spec, first, context) : null;
  const angle = new Set();
  const readings = {};
  for (const [path, name] of byPath) {
    const facts = pathFacts(context, path);
    if (facts.angle) angle.add(name);
    if (!scaled)
      readings[name] =
        facts.angle && (widget === "sparkline" || widget === "chart")
          ? reading(path, angleRange(path, facts.storeUnit))
          : reading(path);
    else if (widget === "level" && path === first)
      readings[name] = reading(path, scale, levelZones(path, scale));
    else readings[name] = reading(path, scaleOf(spec, path, context));
  }
  let body;
  if (widget === "gauge") body = gauge(names, scale);
  else if (widget === "dial") body = dial(names, scale);
  else if (widget === "level")
    body = level(names, LOW_GOOD_PATH.test(first ?? ""));
  else if (widget === "sparkline") body = sparkline(names, angle);
  else if (widget === "chart") body = chart(names, byPath, context, angle);
  else body = tile(names);
  const subtitled =
    typeof spec.subtitle === "string" && spec.subtitle.length > 0;
  const html = h(
    "div",
    { class: `tp in in-${widget}`, ...domainOf(pageDomainFor(first)) },
    subtitled ? h("p", { class: "in-dv", "data-tc-device": names[0] }) : "",
    body.html,
    FRESHNESS_LINE,
  );
  const history = widget === "sparkline" || widget === "chart";
  return output(`instrument.${widget}`, 1, {
    html,
    css:
      COMMON_CSS +
      (widget === "chart" ? "" : READOUT_CSS) +
      (history ? CHIP_CSS + AREA_CSS : "") +
      body.css +
      FRESHNESS_CSS +
      domainCss(body.domains ?? domainsOf(paths)),
    readings,
    card: { kind: "panel", icon: widget },
  });
}
